import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaBolt, FaMapMarkerAlt, FaTruck } from "react-icons/fa";
import { productService, resolveProductMediaUrl } from "../services/apiService";
import { useLocationPreference } from "../contexts/LocationPreferenceContext";
import { isValidGeoCoord, parseCoord } from "../utils/geo";
import { formatDeliveryDateTime, resolveDeliveryDateTime } from "../utils/deliveryFormat";
import {
  buildCatalogSearchParams,
  persistExpressPreference,
  readExpressPreference,
} from "../utils/searchFilters";
import { useThemeClasses } from "../hooks/useThemeClasses";

const MarketplaceProductSearch = () => {
  const { isDark, sc } = useThemeClasses();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const query = params.get("q") || "";

  const {
    searchLocation,
    loading: isLocationLoading,
    showPrompt: isLocationPromptOpen,
    radiusKm,
    reopenPrompt,
  } = useLocationPreference();

  const [expressDelivery, setExpressDelivery] = useState(() => readExpressPreference(location.search));
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const effectiveCoords = useMemo(() => {
    if (isValidGeoCoord(searchLocation?.lat, searchLocation?.lng)) {
      return searchLocation;
    }
    const urlLat = parseCoord(params.get("lat"));
    const urlLng = parseCoord(params.get("lng"));
    if (isValidGeoCoord(urlLat, urlLng)) {
      return { lat: urlLat, lng: urlLng };
    }
    return null;
  }, [searchLocation, params]);

  useEffect(() => {
    setExpressDelivery(readExpressPreference(location.search));
  }, [location.search]);

  const syncExpressInUrl = (enabled, coords = effectiveCoords) => {
    persistExpressPreference(enabled);
    const next = buildCatalogSearchParams({
      query,
      express: enabled,
      coords: isValidGeoCoord(coords?.lat, coords?.lng) ? coords : null,
      radiusKm,
      existingSearch: location.search,
    });
    navigate(`/catalog?${next.toString()}`, { replace: true });
  };

  useEffect(() => {
    if (!expressDelivery || !query.trim()) return;
    if (!isValidGeoCoord(effectiveCoords?.lat, effectiveCoords?.lng)) return;

    const urlLat = parseCoord(params.get("lat"));
    const urlLng = parseCoord(params.get("lng"));
    if (isValidGeoCoord(urlLat, urlLng)) return;

    syncExpressInUrl(true, effectiveCoords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expressDelivery, effectiveCoords, query]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setProducts([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const filters = { search: q, limit: 40, page: 1 };
        const hasCoords = isValidGeoCoord(effectiveCoords?.lat, effectiveCoords?.lng);
        if (expressDelivery && hasCoords) {
          filters.expressDelivery = true;
          filters.latitude = effectiveCoords.lat;
          filters.longitude = effectiveCoords.lng;
          filters.radiusKm = radiusKm;
        }
        const response = await productService.getUserProducts(filters);
        if (cancelled) return;
        const list = Array.isArray(response?.products) ? response.products : [];
        setProducts(list);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Could not load products");
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, expressDelivery, effectiveCoords, radiusKm]);

  if (!query.trim()) return null;

  const expressReady = isValidGeoCoord(effectiveCoords?.lat, effectiveCoords?.lng);
  const expressCount = products.filter((p) => p.expressAvailable).length;
  const nearestShopCount = products.filter((p) => !p.expressAvailable).length;

  return (
    <section className={sc("searchResultsPanel")}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            Search results for &ldquo;{query}&rdquo;
          </h2>
        </div>
        <label className={sc("searchResultsExpressToggle")}>
          <input
            type="checkbox"
            checked={expressDelivery}
            onChange={(e) => {
              setExpressDelivery(e.target.checked);
              syncExpressInUrl(e.target.checked);
            }}
            className="rounded border-slate-500 text-amber-500 focus:ring-amber-500"
          />
          <FaBolt className={`text-sm ${isDark ? "text-amber-400" : "text-amber-600"}`} />
          <span className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
            Express delivery
          </span>
        </label>
      </div>

      {expressDelivery && isLocationLoading && (
        <p className={`text-sm mb-3 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          Detecting your location…
        </p>
      )}
      {expressDelivery && !isLocationLoading && isLocationPromptOpen && (
        <p className={`text-sm mb-3 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
          Choose your location to use express delivery.{" "}
          <button type="button" onClick={reopenPrompt} className="underline font-semibold">
            Choose now
          </button>
        </p>
      )}
      {expressDelivery && !expressReady && !isLocationLoading && !isLocationPromptOpen && (
        <p className={`text-sm mb-3 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
          Enable location access or add a pincode to your profile for express delivery. Showing all
          matching products until your location is available.
        </p>
      )}
      {error && (
        <p className={`text-sm mb-3 ${isDark ? "text-rose-400" : "text-rose-600"}`}>{error}</p>
      )}
      {loading && (
        <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          Searching products…
        </p>
      )}

      {!loading && !error && products.length === 0 && (
        <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          {expressDelivery ? "No products found for this search." : "No products found."}
        </p>
      )}

      {!loading && products.length > 0 && (
        <>
          {expressDelivery && expressReady && (
            <p className={`text-xs mb-3 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
              {expressCount > 0
                ? `${expressCount} available for express within ${radiusKm} km`
                : "None within express range"}
              {nearestShopCount > 0 ? ` · ${nearestShopCount} via nearest shop` : ""}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => {
              const id = product._id || product.id;
              const image =
                resolveProductMediaUrl(product.imageUrl || product.images?.[0]) ||
                "https://via.placeholder.com/200?text=Part";
              const deliveryLabel = formatDeliveryDateTime(resolveDeliveryDateTime(product));
              return (
                <Link
                  key={id}
                  to={`/catalog/part-p-${id}`}
                  state={{ product: { ...product, id, image: image, images: product.images } }}
                  className={sc("searchResultsCard")}
                >
                  <div className="flex gap-3">
                    <img
                      src={image}
                      alt={product.name}
                      className={`h-20 w-20 rounded-lg object-cover shrink-0 ${
                        isDark ? "border border-slate-700" : "border border-slate-200"
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-sm font-semibold line-clamp-2 ${
                          isDark ? "text-white" : "text-slate-900"
                        }`}
                      >
                        {product.name}
                      </p>
                      <p className={`text-xs mt-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                        {product.category || "Part"}
                      </p>
                      <p
                        className={`text-sm font-bold mt-2 ${
                          isDark ? "text-amber-400" : "text-amber-700"
                        }`}
                      >
                        ₹{Number(product.price || 0).toLocaleString("en-IN")}
                      </p>
                      {expressDelivery && expressReady && product.expressAvailable && (
                        <p
                          className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold ${
                            isDark ? "text-emerald-400" : "text-emerald-600"
                          }`}
                        >
                          <FaBolt className="text-[10px]" />
                          Express · by {deliveryLabel}
                        </p>
                      )}
                      {expressDelivery && expressReady && !product.expressAvailable && (
                        <div className="mt-2 space-y-1">
                          {product.nearestShop && (
                            <p
                              className={`text-[11px] leading-snug ${
                                isDark ? "text-slate-400" : "text-slate-500"
                              }`}
                            >
                              <FaMapMarkerAlt className="inline text-amber-500 mr-1" />
                              Nearest shop: {product.nearestShop.name}
                              {product.nearestShop.city ? `, ${product.nearestShop.city}` : ""}
                              {product.nearestShop.distanceKm != null
                                ? ` (${product.nearestShop.distanceKm} km)`
                                : ""}
                            </p>
                          )}
                          <p
                            className={`text-[11px] font-semibold ${
                              isDark ? "text-amber-300/90" : "text-amber-700"
                            }`}
                          >
                            <FaTruck
                              className={`inline mr-1 ${isDark ? "text-amber-400" : "text-amber-600"}`}
                            />
                            Expected delivery: {deliveryLabel}
                            {product.deliveryDays ? ` (${product.deliveryDays} days)` : ""}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};

export default MarketplaceProductSearch;

